import type { DocSchema } from './schema';

/** 산업안전보건관리비 사용 항목 — 「건설업 산업안전보건관리비 계상 및 사용기준」 고시의 9 항목. */
const COST_ITEMS = [
  '안전관리자·보건관리자 인건비 및 업무수당',
  '안전시설비',
  '보호구',
  '안전보건진단비',
  '안전보건교육비 및 행사비',
  '근로자 건강장해예방비',
  '건설재해예방전문지도기관 기술지도비',
  '본사 전담조직 근로자 임금',
  '위험성평가 등에 따른 소요비용',
];

/** 목표 달성도 · 후속조치 진행 — 같은 단계. */
const PROGRESS = ['계획', '진행', '완료', '미흡'];

/** 양식 문서 칸 정의 — 안전보건경영 · 위험성평가(DOC-136). */
export const MANAGEMENT_DOCS: Record<'POLICY' | 'ORG' | 'COST' | 'RISK_PLAN' | 'RISK_MEETING' | 'HAZARD_SURVEY', DocSchema> = {
  /** 안전보건 목표 및 방침 — 경영방침 · 연간 목표 표 · 공표. */
  POLICY: {
    code: 'POLICY',
    titleKey: 'title',
    sections: [
      {
        kind: 'fields',
        fields: [
          { key: 'title', label: '제목', wide: true, initial: '안전보건 목표 및 경영방침' },
          { key: 'year', label: '적용 연도', kind: 'number' },
          { key: 'period', label: '적용 기간' },
          { key: 'ceo', label: '대표이사' },
          { key: 'siteManager', label: '현장 총괄책임자' },
        ],
      },
      {
        kind: 'fields',
        label: '안전보건 경영방침',
        fields: [
          { key: 'vision', label: '비전', wide: true },
          { key: 'policy', label: '경영방침', kind: 'textarea', wide: true, rows: 6 },
        ],
      },
      {
        kind: 'table',
        key: 'goals',
        label: '연간 안전보건 목표',
        columns: [
          { key: 'goal', label: '목표', width: '20%' },
          { key: 'plan', label: '세부 추진계획' },
          { key: 'indicator', label: '성과 지표', width: '14%' },
          { key: 'owner', label: '담당', width: '10%' },
          { key: 'due', label: '기한', kind: 'date', width: '12%' },
          { key: 'progress', label: '달성도', kind: 'select', options: PROGRESS, width: '9%' },
          { key: 'note', label: '비고', width: '10%' },
        ],
      },
      {
        kind: 'fields',
        label: '공표',
        fields: [
          { key: 'announcedOn', label: '공표일', kind: 'date' },
          { key: 'postedAt', label: '게시 장소' },
          { key: 'method', label: '공표 방법', kind: 'select', options: ['게시판 게시', '교육 시 전달', '회의 시 공유', '문자·메신저'] },
          { key: 'reviewOn', label: '검토 예정일', kind: 'date' },
        ],
      },
    ],
  },

  /** 안전보건조직 — 선임 현황 표 · 협력업체 안전책임자 · 비상연락망. */
  ORG: {
    code: 'ORG',
    titleKey: 'title',
    sections: [
      {
        kind: 'fields',
        fields: [
          { key: 'title', label: '제목', wide: true, initial: '안전보건관리 조직 현황' },
          { key: 'baseDate', label: '기준일', kind: 'date' },
          { key: 'workers', label: '상시 근로자 수', kind: 'number' },
        ],
      },
      {
        kind: 'table',
        key: 'members',
        label: '안전보건관리 조직',
        columns: [
          {
            key: 'role',
            label: '직책',
            kind: 'select',
            options: ['안전보건총괄책임자', '안전관리자', '보건관리자', '관리감독자', '안전보건관리담당자', '산업보건의'],
            width: '16%',
          },
          { key: 'name', label: '성명', width: '10%' },
          { key: 'dept', label: '소속·직위', width: '14%' },
          { key: 'phone', label: '연락처', width: '13%' },
          { key: 'appointedOn', label: '선임일', kind: 'date', width: '12%' },
          { key: 'license', label: '자격', width: '14%' },
          { key: 'duty', label: '담당 업무' },
        ],
        initialRows: [
          { role: '안전보건총괄책임자' },
          { role: '안전관리자' },
          { role: '보건관리자' },
          { role: '관리감독자' },
        ],
      },
      {
        kind: 'table',
        key: 'vendors',
        label: '협력업체 안전책임자',
        columns: [
          { key: 'company', label: '업체명', width: '16%' },
          { key: 'trade', label: '공종', width: '12%' },
          { key: 'name', label: '성명', width: '10%' },
          { key: 'position', label: '직위', width: '10%' },
          { key: 'phone', label: '연락처', width: '13%' },
          { key: 'appointedOn', label: '선임일', kind: 'date', width: '12%' },
          { key: 'note', label: '비고' },
        ],
      },
      {
        kind: 'table',
        key: 'emergency',
        label: '비상연락망',
        columns: [
          { key: 'org', label: '구분', width: '18%' },
          { key: 'name', label: '기관·담당자', width: '20%' },
          { key: 'phone', label: '연락처', width: '18%' },
          { key: 'note', label: '비고' },
        ],
        initialRows: [
          { org: '소방서', phone: '119' },
          { org: '경찰서', phone: '112' },
          { org: '병원' },
          { org: '고용노동부 지청' },
          { org: '한국산업안전보건공단' },
          { org: '본사 안전팀' },
        ],
      },
    ],
  },

  /** 산업안전보건관리비 — 계상 · 사용내역 표(고시 9 항목) · 누계. */
  COST: {
    code: 'COST',
    titleKey: 'title',
    sections: [
      {
        kind: 'fields',
        fields: [
          { key: 'title', label: '제목', wide: true, initial: '산업안전보건관리비 사용내역' },
          { key: 'month', label: '사용 월' },
          { key: 'periodFrom', label: '기간 시작', kind: 'date' },
          { key: 'periodTo', label: '기간 끝', kind: 'date' },
        ],
      },
      {
        kind: 'fields',
        label: '계상',
        fields: [
          { key: 'contractAmount', label: '공사금액(원)', kind: 'number' },
          {
            key: 'workType',
            label: '공사 종류',
            kind: 'select',
            options: ['건축공사', '토목공사', '중건설공사', '특수건설공사'],
          },
          { key: 'rate', label: '계상 요율(%)', kind: 'number' },
          { key: 'baseAmount', label: '기초액(원)', kind: 'number' },
          { key: 'budget', label: '계상액(원)', kind: 'number' },
        ],
      },
      {
        kind: 'table',
        key: 'uses',
        label: '사용내역',
        columns: [
          { key: 'date', label: '사용일', kind: 'date', width: '11%' },
          { key: 'item', label: '사용 항목', kind: 'select', options: COST_ITEMS, width: '24%' },
          { key: 'detail', label: '내용' },
          { key: 'amount', label: '금액(원)', kind: 'number', width: '12%' },
          { key: 'vendor', label: '거래처', width: '12%' },
          { key: 'evidence', label: '증빙', kind: 'select', options: ['세금계산서', '영수증', '카드전표', '급여대장', '기타'], width: '11%' },
        ],
      },
      {
        kind: 'fields',
        label: '누계',
        fields: [
          { key: 'thisMonth', label: '당월 사용액(원)', kind: 'number' },
          { key: 'prevTotal', label: '전월까지 누계(원)', kind: 'number' },
          { key: 'total', label: '누계 사용액(원)', kind: 'number' },
          { key: 'useRate', label: '사용률(%)', kind: 'number' },
          { key: 'progressRate', label: '공정률(%)', kind: 'number' },
          { key: 'remark', label: '비고', kind: 'textarea', wide: true, rows: 2 },
        ],
      },
    ],
  },

  /** 위험성평가 계획서 — 구분 · 대상 · 방법 · 평가팀 · 근로자 참여 · 교육. */
  RISK_PLAN: {
    code: 'RISK_PLAN',
    titleKey: 'title',
    sections: [
      {
        kind: 'fields',
        fields: [
          { key: 'title', label: '제목', wide: true, initial: '위험성평가 실시 계획서' },
          { key: 'assessType', label: '평가 구분', kind: 'select', options: ['최초평가', '정기평가', '수시평가', '상시평가'] },
          { key: 'periodFrom', label: '평가 기간 시작', kind: 'date' },
          { key: 'periodTo', label: '평가 기간 끝', kind: 'date' },
          { key: 'trades', label: '대상 공종', wide: true },
          { key: 'scope', label: '평가 대상 작업·장소', kind: 'textarea', wide: true, rows: 3 },
        ],
      },
      {
        kind: 'fields',
        label: '실시 방법',
        fields: [
          {
            key: 'method',
            label: '평가 기법',
            kind: 'select',
            options: ['3단계 판단법', '빈도·강도법', '체크리스트법', '핵심요인 기술법'],
          },
          { key: 'riskCriteria', label: '허용 가능 위험 기준' },
          { key: 'procedure', label: '실시 절차', kind: 'textarea', wide: true, rows: 4 },
        ],
      },
      {
        kind: 'table',
        key: 'team',
        label: '평가팀',
        columns: [
          { key: 'role', label: '역할', kind: 'select', options: ['총괄', '실시 책임', '평가자', '근로자 대표', '협력업체', '기타'], width: '14%' },
          { key: 'name', label: '성명', width: '12%' },
          { key: 'dept', label: '소속·직위', width: '18%' },
          { key: 'duty', label: '담당 업무' },
        ],
        initialRows: [{ role: '총괄' }, { role: '실시 책임' }, { role: '평가자' }, { role: '근로자 대표' }],
      },
      {
        kind: 'fields',
        label: '근로자 참여',
        fields: [
          { key: 'participation', label: '참여 방법', kind: 'textarea', wide: true, rows: 3 },
          { key: 'shareMethod', label: '결과 공유 방법', kind: 'textarea', wide: true, rows: 2 },
        ],
      },
      {
        kind: 'table',
        key: 'training',
        label: '교육 계획',
        columns: [
          { key: 'target', label: '대상', width: '18%' },
          { key: 'content', label: '교육 내용' },
          { key: 'date', label: '일자', kind: 'date', width: '12%' },
          { key: 'hours', label: '시간', kind: 'number', width: '8%' },
          { key: 'instructor', label: '강사', width: '12%' },
        ],
      },
    ],
  },

  /** 위험성평가 회의록 — 일시 · 참석자 · 안건 · 논의 · 결정 · 후속조치. */
  RISK_MEETING: {
    code: 'RISK_MEETING',
    titleKey: 'title',
    sections: [
      {
        kind: 'fields',
        fields: [
          { key: 'title', label: '제목', wide: true, initial: '위험성평가 회의록' },
          { key: 'meetingType', label: '회의 구분', kind: 'select', options: ['최초평가', '정기평가', '수시평가', '상시평가(월간)', '상시평가(주간)', '결과 공유'] },
          { key: 'date', label: '일자', kind: 'date' },
          { key: 'startTime', label: '시작', kind: 'time' },
          { key: 'endTime', label: '끝', kind: 'time' },
          { key: 'place', label: '장소' },
          { key: 'chair', label: '주관' },
        ],
      },
      {
        kind: 'table',
        key: 'attendees',
        label: '참석자',
        columns: [
          { key: 'company', label: '소속', width: '22%' },
          { key: 'position', label: '직위·직책', width: '18%' },
          { key: 'name', label: '성명', width: '16%' },
          { key: 'note', label: '비고' },
        ],
      },
      {
        kind: 'fields',
        label: '회의 내용',
        fields: [
          { key: 'agenda', label: '안건', kind: 'textarea', wide: true, rows: 3 },
          { key: 'discussion', label: '논의 내용', kind: 'textarea', wide: true, rows: 6 },
          { key: 'decision', label: '결정 사항', kind: 'textarea', wide: true, rows: 4 },
        ],
      },
      {
        kind: 'table',
        key: 'actions',
        label: '후속조치',
        columns: [
          { key: 'item', label: '조치 사항' },
          { key: 'owner', label: '담당', width: '12%' },
          { key: 'due', label: '기한', kind: 'date', width: '12%' },
          { key: 'status', label: '진행', kind: 'select', options: PROGRESS, width: '9%' },
          { key: 'doneOn', label: '완료일', kind: 'date', width: '12%' },
        ],
      },
    ],
  },

  /** 위험요인 정보조사표 — 조사 정보 · 작업별 위험요인 표. */
  HAZARD_SURVEY: {
    code: 'HAZARD_SURVEY',
    titleKey: 'trade',
    sections: [
      {
        kind: 'fields',
        fields: [
          { key: 'trade', label: '공종', wide: true },
          { key: 'date', label: '조사일', kind: 'date' },
          { key: 'surveyor', label: '조사자' },
          { key: 'company', label: '협력업체' },
          { key: 'location', label: '작업 장소' },
          { key: 'workers', label: '작업 인원', kind: 'number' },
        ],
      },
      {
        kind: 'table',
        key: 'hazards',
        label: '위험요인 정보',
        columns: [
          { key: 'task', label: '세부 작업', width: '13%' },
          { key: 'equipment', label: '기계·기구·설비', width: '13%' },
          { key: 'substance', label: '유해·위험물질', width: '12%' },
          { key: 'environment', label: '작업환경', width: '12%' },
          {
            key: 'category',
            label: '위험 분류',
            kind: 'select',
            options: ['기계적', '전기적', '화학적', '생물학적', '작업특성', '작업환경'],
            width: '10%',
          },
          { key: 'hazard', label: '위험요인' },
          { key: 'pastAccident', label: '과거 재해·아차사고', width: '14%' },
        ],
      },
      {
        kind: 'fields',
        label: '참고 자료',
        fields: [
          { key: 'msds', label: 'MSDS 확보', kind: 'check' },
          { key: 'workPlan', label: '작업계획서 확인', kind: 'check' },
          { key: 'manual', label: '작업 표준·매뉴얼 확인', kind: 'check' },
          { key: 'remark', label: '비고', kind: 'textarea', wide: true, rows: 2 },
        ],
      },
    ],
  },
};
